import React from "react";
import { usePlayer, usePlayers, useGame } from "@empirica/core/player/classic/react";
import { getTaskConfig } from "../utils/taskConfig.js";
import { getNicknameFromId } from "../components/Avatar.jsx";
import { isBaseRateStaticSyntheticGroupCondition } from "../utils/conditionUtils.js";

function getPlayerLabel(player) {
  return player.get("nickname") || `Player ${String(player.id || "").slice(-4)}`;
}

export function Result() {
  const player = usePlayer();
  const players = usePlayers();
  const game = useGame();
  const treatment = game.get("treatment") || {};
  const taskConfig = getTaskConfig(game.get("treatment") || {});
  const isBaseRateStaticSyntheticGroup =
    isBaseRateStaticSyntheticGroupCondition(treatment);
  const staticSyntheticPeersRaw = isBaseRateStaticSyntheticGroup
    ? game.get("staticSyntheticPeers") || []
    : [];
  const staticSyntheticPeers = Array.isArray(staticSyntheticPeersRaw)
    ? staticSyntheticPeersRaw
    : [];

  React.useEffect(() => {
    if (isBaseRateStaticSyntheticGroup) {
      player?.set?.("readyToExit", true);
    }
  }, [isBaseRateStaticSyntheticGroup, player]);

  if (isBaseRateStaticSyntheticGroup) {
    return null;
  }

  const options = taskConfig.options || [];
  const predictionInput = taskConfig.predictionInput || {};
  const isNumericPrediction = predictionInput.type === "number";

  const normalizePredictionChoice = (value) => {
    if (typeof taskConfig.normalizePredictionValue === "function") {
      return taskConfig.normalizePredictionValue(value);
    }
    return String(value ?? "").trim();
  };

  const formatBelief = (value) => {
    if (typeof taskConfig.formatPredictionDisplay === "function") {
      return taskConfig.formatPredictionDisplay(value);
    }
    return String(value ?? "").trim();
  };

  const playerBeliefs = game.get("playerBeliefs") || {};

  const getBeliefForPlayer = (player, voteType) => {
    if (voteType === "initialBelief") {
      return (
        player.round?.get?.("initialBelief") ??
        playerBeliefs[player.id]?.initialBelief ??
        null
      );
    }

    return (
      player.round?.get?.("finalBelief") ??
      playerBeliefs[player.id]?.finalBelief ??
      null
    );
  };

  if (isNumericPrediction) {
    const numericRows = [
      ...players.map((player) => {
        const initialBelief = getBeliefForPlayer(player, "initialBelief");
        const finalBelief = getBeliefForPlayer(player, "finalBelief");
        return {
          id: player.id,
          name: getPlayerLabel(player),
          initialBelief,
          finalBelief,
          finalNormalized: normalizePredictionChoice(finalBelief),
        };
      }),
      ...staticSyntheticPeers.map((peer) => {
        const initialBelief = peer?.initialBelief ?? playerBeliefs[peer.id]?.initialBelief ?? null;
        const finalBelief = peer?.finalBelief ?? playerBeliefs[peer.id]?.finalBelief ?? initialBelief;
        return {
          id: peer.id,
          name: peer?.name || getNicknameFromId(peer.id),
          initialBelief,
          finalBelief,
          finalNormalized: normalizePredictionChoice(finalBelief),
        };
      }),
    ];

    const validFinalValues = numericRows
      .map((row) => row.finalNormalized)
      .filter((value) => value !== "");
    const finalCounts = validFinalValues.reduce((acc, value) => {
      acc[value] = (acc[value] || 0) + 1;
      return acc;
    }, {});
    const exactMajorityEntry =
      Object.entries(finalCounts).find(([, count]) => count >= 2) || null;
    const majorityValue = exactMajorityEntry?.[0] || "";
    const majorityCount = exactMajorityEntry?.[1] || 0;
    const allThreeDifferentNoMajority =
      !majorityValue &&
      validFinalValues.length === numericRows.length &&
      new Set(validFinalValues).size === numericRows.length;

    return (
      <div className="max-w-4xl mx-auto p-6">
        <h2 className="text-2xl font-bold mb-6">Results Summary</h2>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-xl font-semibold mb-3">Group Majority Status</h3>
          {majorityValue ? (
            <p className="text-lg text-gray-900">
              Group majority vote: <strong>{formatBelief(majorityValue)}</strong> ({majorityCount} of {numericRows.length} players)
            </p>
          ) : allThreeDifferentNoMajority ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-amber-900 font-medium">
                No group majority vote formed because all three final percentage estimates were different.
              </p>
              <p className="text-amber-800 text-sm mt-1">
                No group-accuracy bonus is awarded in this case.
              </p>
            </div>
          ) : (
            <p className="text-gray-600">
              A group majority vote could not be determined from the recorded final estimates.
            </p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-4">Player Estimates</h3>
          <div className="space-y-3">
            {numericRows.map((row) => {
              const majorityStatus = majorityValue
                ? row.finalNormalized === majorityValue
                  ? "Matches group majority"
                  : "Not in group majority"
                : allThreeDifferentNoMajority
                ? "No majority formed"
                : "Majority unavailable";

              return (
                <div key={row.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="font-semibold text-gray-900 mb-2">{row.name}</div>
                  <div className="text-sm text-gray-700">
                    Initial estimate: <strong>{row.initialBelief !== null && row.initialBelief !== undefined && String(row.initialBelief).trim() !== "" ? formatBelief(row.initialBelief) : "—"}</strong>
                  </div>
                  <div className="text-sm text-gray-700">
                    Final estimate: <strong>{row.finalBelief !== null && row.finalBelief !== undefined && String(row.finalBelief).trim() !== "" ? formatBelief(row.finalBelief) : "—"}</strong>
                  </div>
                  <div className="text-sm font-medium text-gray-900 mt-2">{majorityStatus}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-gray-600 text-sm">
            After the time runs out, you will automatically proceed to the exit survey.
          </p>
        </div>
      </div>
    );
  }

  // Check if this is a Synthetic Peers condition
  const isSyntheticPeers = game.get("isSyntheticPeers") === true;

  // Get synthetic players' beliefs
  const getSyntheticPlayersBeliefs = () => {
    if (!isSyntheticPeers) return [];

    const syntheticBeliefs = [];
    Object.keys(playerBeliefs).forEach((playerId) => {
      if (playerId.startsWith("synthetic_") && playerBeliefs[playerId]?.isSynthetic) {
        syntheticBeliefs.push({
          id: playerId,
          initialBelief: playerBeliefs[playerId].initialBelief,
          finalBelief: playerBeliefs[playerId].finalBelief || playerBeliefs[playerId].initialBelief,
        });
      }
    });
    return syntheticBeliefs;
  };

  const syntheticPlayers = getSyntheticPlayersBeliefs();

  function countVotes(voteType) {
    const humanVotes = players.map((player) => getBeliefForPlayer(player, voteType));
    const syntheticVotes = syntheticPlayers.map((syntheticPlayer) =>
      voteType === "initialBelief"
        ? syntheticPlayer.initialBelief
        : syntheticPlayer.finalBelief
    );

    const allVotes = [...humanVotes, ...syntheticVotes];
    const validVotes = allVotes.filter((value) => options.some((option) => option.value === value));
    const counts = options.reduce((acc, option) => {
      acc[option.value] = validVotes.filter((value) => value === option.value).length;
      return acc;
    }, {});
    return {
      counts,
      total: validVotes.length,
    };
  }

  const initialVotes = countVotes("initialBelief");
  const finalVotes = countVotes("finalBelief");

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6">Results Summary</h2>

      <div className="grid grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-4">Initial Predictions</h3>
          {initialVotes.total > 0 ? (
            <div className="space-y-3">
              <ul className="space-y-2">
                {options.map((option) => (
                  <li key={option.value}>{option.label}: {initialVotes.counts[option.value] || 0} votes</li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-gray-600 italic">
              No initial predictions captured for this treatment condition.
            </p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-4">Final Predictions</h3>
          <div className="space-y-3">
            <ul className="space-y-2">
              {options.map((option) => (
                <li key={option.value}>{option.label}: {finalVotes.counts[option.value] || 0} votes</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="mt-8 text-center">
        <p className="text-gray-600 text-sm">
          After the time runs out, you will automatically proceed to the exit survey.
        </p>
      </div>
    </div>
  );
}
